import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  buildPlannedLessons,
  createPlannedInApp,
  preflightCurriculumBuild,
  verifyPlannedLessons,
} from '@platform/client';
import {
  buildPreflightSchema,
  curriculumVerifySchema,
  MAX_BUILD,
} from '@platform/schema/curriculum';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';
import { counted } from '../library/counted';

import { batchOf, STATUS_LABEL } from './actions';
import { ResearchOption } from './ResearchOption';

import type { BuildPreflight } from '@platform/schema/curriculum';
import type { ReactNode } from 'react';

const preflightQuery = (ids: readonly string[]) => ({
  queryKey: ['curriculum', 'preflight', ids],
  enabled: ids.length > 0,
  // Asked on every open: a lesson queued from another tab changes the answer.
  staleTime: 0,
  queryFn: async () =>
    buildPreflightSchema.parse(
      (
        await preflightCurriculumBuild({
          body: { lessonIds: [...ids] },
          throwOnError: true,
        })
      ).data,
    ),
});

/**
 * Build the selected lessons — spec §7. The preflight runs twice when the selection is
 * not all buildable: once on everything, to say what is set aside, and once on the batch,
 * so the courses, modules and estimate describe what will actually run.
 */
export function BuildDialog({
  ids,
  verify = false,
  onClose,
  onBuilt,
  onEditStory,
}: {
  ids: readonly string[] | null;
  /** Verify each lesson against its sources once it is built — CURR-31. */
  verify?: boolean;
  onClose: () => void;
  onBuilt: (jobId: string) => void;
  /** Open a module's story, from the note naming it as drafted — CURR-58. */
  onEditStory: (moduleId: string) => void;
}) {
  const queries = useQueryClient();
  const [research, setResearch] = useState(false);
  const all = useQuery(preflightQuery(ids ?? []));
  const batch = all.data === undefined ? [] : batchOf(ids ?? [], all.data);
  const same = batch.length === (ids ?? []).length;
  const run = useQuery({
    ...preflightQuery(batch),
    enabled: !same && batch.length > 0,
  });
  const plan = same ? all.data : run.data;

  const build = useTrackedMutation(verb(verify), {
    mutationFn: async (lessonIds: string[]) =>
      (
        await buildPlannedLessons({
          body: { lessonIds, verify, research },
          throwOnError: true,
        })
      ).data,
    onSuccess: async (made) => {
      onBuilt(made.jobId);
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
      onClose();
    },
  });

  const error = all.error ?? run.error;

  return (
    <Dialog open={ids !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{verb(verify)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {error !== null && (
            <Alert severity="error">{describeApiError(error)}</Alert>
          )}
          {all.data !== undefined && (
            <DraftedModules preflight={all.data} onOpen={onEditStory} />
          )}
          {all.data === undefined ? (
            all.isPending && <Typography>Checking the selection…</Typography>
          ) : (
            <SetAside preflight={all.data} batch={batch.length} />
          )}
          {plan !== undefined && batch.length > 0 && (
            <>
              <Typography>
                This build takes {counted(batch.length, 'lesson')}.
                {verify &&
                  ' Once the lessons are written, the same job checks each against its sources. Building alone never marks a lesson Verified.'}
              </Typography>
              <Created preflight={plan} />
              {plan.warnings.length > 0 && (
                <Listed
                  title="Worth a look first"
                  items={plan.warnings.map(
                    (one) => `${one.lessonId}: ${one.message}`,
                  )}
                />
              )}
              <Typography color="text.secondary">
                {plan.estimate.label}
              </Typography>
              <ResearchOption
                checked={research}
                disabled={build.isPending}
                onChange={setResearch}
              />
            </>
          )}
          {build.isError && (
            <Alert severity="error">{describeApiError(build.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          // Disabled from the first click: a double click is one job. The API returns the
          // same job for the same selection too, so a second request would not add one.
          disabled={plan === undefined || batch.length === 0 || build.isPending}
          onClick={() => {
            build.mutate(batch);
          }}
        >
          {batch.length === 0
            ? 'Nothing to build'
            : `${action(verify)} ${counted(batch.length, 'lesson')}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const action = (verify: boolean): string =>
  verify ? 'Build and verify' : 'Build';
const verb = (verify: boolean): string => `${action(verify)} lessons`;

/**
 * Verify the built lessons among the selected ones against their sources — CURR-31. The
 * API leaves out the lessons with nothing built and names them, so they are listed after.
 */
export function VerifyDialog({
  ids,
  onClose,
  onStarted,
}: {
  ids: readonly string[] | null;
  onClose: () => void;
  onStarted: (jobId: string) => void;
}) {
  const verify = useTrackedMutation('Verify lessons', {
    mutationFn: async (lessonIds: string[]) =>
      curriculumVerifySchema.parse(
        (
          await verifyPlannedLessons({
            body: { lessonIds },
            throwOnError: true,
          })
        ).data,
      ),
    onSuccess: (started) => {
      onStarted(started.jobId);
      if (started.notBuilt.length === 0) close();
    },
  });
  const close = (): void => {
    verify.reset();
    onClose();
  };
  const count = ids?.length ?? 0;
  const done = verify.data;

  return (
    <Dialog open={ids !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Verify lessons</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {done === undefined ? (
            <Typography>
              Checks each built lesson among the{' '}
              {counted(count, 'selected lesson')} against its sources: whether
              each source was reached and read, and which claims it supports.
              Lessons not built yet are left out.
            </Typography>
          ) : (
            <>
              <Alert severity="success">
                Verifying {counted(done.lessons, 'lesson')}.
              </Alert>
              <Listed title="Left out: not built yet" items={done.notBuilt} />
            </>
          )}
          {count > MAX_BUILD && (
            <Alert severity="info">
              A verify job takes at most {MAX_BUILD} lessons. Select fewer.
            </Alert>
          )}
          {verify.isError && (
            <Alert severity="error">{describeApiError(verify.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>
          {done === undefined ? 'Cancel' : 'Close'}
        </Button>
        {done === undefined && (
          <Button
            variant="contained"
            disabled={count === 0 || count > MAX_BUILD || verify.isPending}
            onClick={() => {
              verify.mutate([...(ids ?? [])]);
            }}
          >
            Verify
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** The modules whose story or cast the build drafts first, each opening where it is edited. */
function DraftedModules({
  preflight,
  onOpen,
}: {
  preflight: Pick<BuildPreflight, 'storyMissing' | 'castMissing'>;
  onOpen: (moduleId: string) => void;
}) {
  const missing = new Map<
    string,
    { one: BuildPreflight['storyMissing'][number]; parts: string[] }
  >();
  for (const [part, list] of [
    ['story', preflight.storyMissing],
    ['cast', preflight.castMissing],
  ] as const)
    for (const one of list) {
      const entry = missing.get(one.id) ?? { one, parts: [] };
      entry.parts.push(part);
      missing.set(one.id, entry);
    }
  const modules = [...missing.values()];
  if (modules.length === 0) return null;
  return (
    <Alert severity="info">
      The build writes what these modules are missing before their lessons. You
      can edit it after.
      <Stack component="ul" sx={{ m: 0, pl: 2 }}>
        {modules.map(({ one, parts }) => (
          <li key={one.id}>
            <Link
              component="button"
              onClick={() => {
                onOpen(one.id);
              }}
            >
              {one.name}
            </Link>{' '}
            in {one.course}: {parts.join(' and ')}
          </li>
        ))}
      </Stack>
    </Alert>
  );
}

/** What the selection holds that this build leaves out, and why. */
function SetAside({
  preflight,
  batch,
}: {
  preflight: BuildPreflight;
  batch: number;
}) {
  const kept =
    preflight.count -
    preflight.unavailable.length -
    preflight.blocked.length -
    preflight.unknown.length;

  return (
    <>
      <Typography>{counted(preflight.count, 'lesson')} selected.</Typography>
      {kept > MAX_BUILD && (
        <Alert severity="info">
          A build takes at most {MAX_BUILD} lessons, and{' '}
          {counted(kept, 'selected lesson')} can be built. This one takes the
          first {batch}; build the other {kept - batch} as another batch once it
          has started.
        </Alert>
      )}
      {preflight.unavailable.length > 0 && (
        <Listed
          title="Left out: already queued, building, built or published"
          items={preflight.unavailable.map(
            (one) => `${one.lessonId} — ${STATUS_LABEL[one.status]}`,
          )}
        />
      )}
      {preflight.blocked.length > 0 && (
        <Listed
          title="Left out: cannot be built yet"
          items={preflight.blocked.map(
            (one) => `${one.lessonId}: ${one.issues.join(' ')}`,
          )}
        />
      )}
      {preflight.unknown.length > 0 && (
        <Listed
          title="Left out: no longer in the plan"
          items={preflight.unknown}
        />
      )}
    </>
  );
}

function Created({ preflight }: { preflight: BuildPreflight }) {
  return (
    <>
      <Listed
        title="Courses to create in the app"
        items={preflight.coursesToCreate.map((one) => one.name)}
        none="None. Every course is in the app already."
      />
      <Listed
        title="Modules to create in the app"
        items={preflight.modulesToCreate.map(
          (one) => `${one.course} › ${one.name}`,
        )}
        none="None. Every module is in the app already."
      />
      {preflight.modulesInApp.length > 0 && (
        <Listed
          title="Modules already in the app, reused"
          items={preflight.modulesInApp.map(
            (one) => `${one.course} › ${one.name}`,
          )}
        />
      )}
    </>
  );
}

/**
 * Create the courses and modules the selected lessons belong to — CURR-22's locked
 * proposal. Only the ones not in the app yet; no lesson is built.
 */
export function CreateInAppDialog({
  ids,
  onClose,
}: {
  ids: readonly string[] | null;
  onClose: () => void;
}) {
  const queries = useQueryClient();
  const preflight = useQuery(preflightQuery(ids ?? []));
  const plan = preflight.data;
  const courses = plan?.coursesToCreate.length ?? 0;
  const modules = plan?.modulesToCreate.length ?? 0;

  const create = useTrackedMutation('Create in app', {
    mutationFn: async (request: { courseIds: string[]; moduleIds: string[] }) =>
      createPlannedInApp({ body: request, throwOnError: true }),
    onSuccess: async () => {
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
      onClose();
    },
  });

  return (
    <Dialog open={ids !== null} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create courses and modules in the app</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {preflight.isError && (
            <Alert severity="error">{describeApiError(preflight.error)}</Alert>
          )}
          {plan === undefined ? (
            preflight.isPending && (
              <Typography>Checking the selection…</Typography>
            )
          ) : (
            <>
              <Typography>
                For the {counted(ids?.length ?? 0, 'selected lesson')}. No
                lesson is built.
              </Typography>
              <Created preflight={plan} />
            </>
          )}
          {create.isError && (
            <Alert severity="error">{describeApiError(create.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            plan === undefined || courses + modules === 0 || create.isPending
          }
          onClick={() => {
            if (plan === undefined) return;
            create.mutate({
              courseIds: plan.coursesToCreate.map((one) => one.id),
              moduleIds: plan.modulesToCreate.map((one) => one.id),
            });
          }}
        >
          {courses + modules === 0
            ? 'Nothing to create'
            : `Create ${counted(courses, 'course')} and ${counted(modules, 'module')}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Listed({
  title,
  items,
  none,
}: {
  title: string;
  items: readonly string[];
  none?: ReactNode;
}) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">{title}</Typography>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {none}
        </Typography>
      ) : (
        <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
          {items.map((item) => (
            <Typography component="li" variant="body2" key={item}>
              {item}
            </Typography>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
